import { RubyGemMark } from '../components/RubyGemMark';
import { RubyLoader } from '../components/RubyLoader';
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import PartnerDealsPanel from '../components/PartnerDealsPanel';
import { StaffSubtabs, RUBY, RUBY_DEEP } from '../components/staff/StaffChrome';
import {
  ArrowLeft, User, Mail, Phone, Building, Globe, Calendar, DollarSign,
  Users, TrendingUp, CheckCircle, XCircle, Ban, Clock, Copy, Check,
  Key, AlertTriangle, Link, Hourglass, Wallet, CreditCard, Timer, AlertOctagon,
  FileSignature, FileText, Download, Eye, Trash2, Percent, Building2,
} from 'lucide-react';
import { api } from '../services/apiService';
import { effectiveDealPriceCents } from '../services/dealPricing';
import { academyApi } from '../services/academyApi';
import { documentsApi } from '../services/documentsApi';
import {
  merchantApi, MERCHANT_STATUS_LABEL, MERCHANT_STATUS_CHIP,
  type MerchantApplicationView,
} from '../services/merchantApi';
import { MultiSelectDropdown } from '../components/MultiSelectDropdown';
import DealPickerModal from '../components/DealPickerModal';
import type { AcademyTrack } from '@platform/shared-types';
import { merchantAppUrl } from '../utils/merchantUrl';

/**
 * PARTNER DETAIL — the admin mirror of the partner's own portal.
 *
 * REDESIGNED 2026-09-01. Two things were wrong with the old page and both were
 * structural, not cosmetic:
 *
 * 1. It described the standing referral code in FOUR places — a left-column
 *    code card, a "Deal on the standing link" card, a "Commission Settings"
 *    card, and again as the "Always on" row inside PartnerDealsPanel's deals
 *    table. An admin reading it could not tell whether the always-on code and
 *    the deal links were one system or two. The code, its deal and its
 *    commission now live ONLY on the row that represents it, in Deals & links.
 *
 * 2. Thirteen cards stacked in one scroll inside `max-w-6xl`, so the work an
 *    admin actually comes here to do — assign a deal, read the ledger — was
 *    squeezed into a 2/3 column while a third of the viewport sat empty.
 *
 * The tabs are named after the partner's OWN portal sections (AffiliateLayout's
 * nav) so the two screens describe one partner the same way round.
 */

interface AffiliateDetail {
  id: string;
  name: string;
  email: string;
  phone?: string;
  company?: string;
  website?: string;
  referralCode: string;
  status: 'pending' | 'approved' | 'suspended' | 'rejected';
  commissionMode: 'flat' | 'percentage' | 'both';
  flatFee: number;
  percentageRate: number;
  totalEarnings: number;
  pendingEarnings: number;
  paidEarnings: number;
  // Earnings breakdown by commission status
  clearingEarnings?: number;
  availableEarnings?: number;
  forfeitedEarnings?: number;
  referralCount: number;
  conversionCount: number;
  createdAt: number;
  approvedAt?: number;
  approvedBy?: string;
  suspendedAt?: number;
  suspendedBy?: string;
  suspendReason?: string;
  lastLoginAt?: number;
  assignedDealId?: string;
  paypalEmail?: string;
  /** Merchant-processing access — the same flag the partners list toggles. */
  isInternalIso?: boolean;
  isoCapabilities?: { mccCodes?: string[]; states?: string[]; acceptingOrders?: boolean; note?: string };
  /** documents-service slugs of every agreement/policy this partner must sign. */
  agreementDocumentRefs?: string[];
  /** @deprecated the single-document shape this shipped with first. */
  agreementDocumentRef?: string;
  agreementSetAt?: number;
  agreementSetBy?: string;
  agreementAcceptedAt?: number;
  agreementAcceptedVersionNo?: number;
  // Certification (affiliate-service mirrors this from academy-service).
  // Affiliates approved before this feature existed have no stored attempt
  // and report status 'bypassed' with bypassReason 'legacy'.
  certification?: {
    status: 'not_started' | 'in_progress' | 'failed' | 'passed' | 'bypassed';
    passedAt?: number;
    bestScorePercent?: number;
    bypassedBy?: string;
    bypassedAt?: number;
    bypassReason?: 'admin' | 'legacy';
  };
}

interface LearnerRecord {
  certification?: AffiliateDetail['certification'];
  completedChapterIds?: string[];
  attempts?: Array<{
    attemptId: string;
    // WHICH certification this attempt belongs to. GET /admin/learners/...
    // returns attempts across EVERY track unfiltered (listAttempts is scoped
    // by learner only), so without this the list rendered a Marketing
    // specialisation failure as if it were a failure of the certification
    // that activates partners — the exact question an admin opens this page
    // to answer.
    trackId?: string;
    startedAt: number;
    submittedAt?: number;
    scorePercent?: number;
    passed?: boolean;
  }>;
}

const CERT_LABEL: Record<string, string> = {
  passed: 'Certified',
  in_progress: 'In training',
  failed: 'Failed the exam',
  bypassed: 'Bypassed',
  not_started: 'Not started',
};

// Certification status badge — mirrors getStatusBadge's visual language
// (rounded pill, icon, tri-color) so the two badges read as one system.
const getCertificationBadge = (status?: string) => {
  const s = status || 'not_started';
  switch (s) {
    case 'passed':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 border border-emerald-200">
          <CheckCircle className="w-3 h-3" /> {CERT_LABEL[s]}
        </span>
      );
    case 'in_progress':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 border border-amber-200">
          <Clock className="w-3 h-3" /> {CERT_LABEL[s]}
        </span>
      );
    case 'failed':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 border border-red-200">
          <XCircle className="w-3 h-3" /> {CERT_LABEL[s]}
        </span>
      );
    case 'bypassed':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-stone-100 text-stone-600 border border-stone-200">
          <AlertTriangle className="w-3 h-3" /> {CERT_LABEL[s]}
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-stone-100 text-stone-600 border border-stone-200">
          <Clock className="w-3 h-3" /> {CERT_LABEL.not_started}
        </span>
      );
  }
};

/**
 * A deal as `/admin/deals` returns it TODAY. The page used to read
 * `monthlyAmount` only, which is the deprecated field: a deal priced through
 * the Plan & Packages builder carries `priceCents` and leaves `monthlyAmount`
 * unset, so every modern deal rendered here as "$0.00/mo". Price now goes
 * through `effectiveDealPriceCents`, the same helper DealsPage and the deal
 * editor use, and the location/package terms that decide what a referral
 * actually pays are shown alongside it.
 */
interface Deal {
  id: string;
  name: string;
  code: string;
  /** @deprecated superseded by `priceCents` — read via effectiveDealPriceCents. */
  monthlyAmount?: number;
  priceCents?: number;
  trialDays: number;
  commissionMode: 'flat' | 'percentage' | 'both';
  flatFee?: number;
  percentageRate?: number;
  isActive: string;
  isDefaultDeal?: boolean;
  /** Locations included in the deal price. */
  freeLocationQuota?: number;
  /** Monthly price per location beyond the quota, integer cents. */
  additionalLocationPriceCents?: number;
  packageIds?: string[];
  deviceSkuIds?: string[];
  devicePackageIds?: string[];
}

/** Cents -> "$1,234.00". Same presentation as the deals list. */
const dealMoney = (cents: number): string =>
  `$${((cents || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

interface Referral {
  id: string;
  leadEmail: string;
  leadName?: string;
  tenantId?: string;
  // Commission status flow: pending -> clearing -> available -> paid (or forfeited)
  status: 'pending' | 'clearing' | 'available' | 'paid' | 'forfeited' | 'converted' | 'expired';
  createdAt: number;
  conversionAt?: number;
  convertedAt?: number; // Legacy alias
  // Commission amount in cents
  commission?: number;
  commissionAmount?: number; // Legacy alias
  // Clearing period fields
  clearingDate?: number;
  clearingDays?: number;
  clearedAt?: number;
  // Payout fields
  paidAt?: number;
  payoutId?: string;
  // Forfeiture fields
  forfeitedAt?: number;
  forfeitReason?: string;
}

interface Payout {
  id: string;
  affiliateId: string;
  amount: number;
  status: 'pending' | 'processing' | 'processed' | 'completed' | 'failed';
  paypalEmail?: string;
  requestedAt: number;
  processedAt?: number;
  processedBy?: string;
  transactionId?: string;
  notes?: string;
}

// Status badge helper for affiliate status
const getStatusBadge = (status: string, size: 'sm' | 'lg' = 'sm') => {
  const baseClasses = size === 'lg'
    ? 'inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium'
    : 'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium';

  switch (status) {
    case 'approved':
      return (
        <span className={`${baseClasses} bg-emerald-100 text-emerald-700 border border-emerald-200`}>
          <CheckCircle className={size === 'lg' ? 'w-4 h-4' : 'w-3 h-3'} /> Approved
        </span>
      );
    case 'pending':
      return (
        <span className={`${baseClasses} bg-amber-100 text-amber-700 border border-amber-200`}>
          <Clock className={size === 'lg' ? 'w-4 h-4' : 'w-3 h-3'} /> Pending
        </span>
      );
    case 'suspended':
      return (
        <span className={`${baseClasses} bg-red-100 text-red-700 border border-red-200`}>
          <Ban className={size === 'lg' ? 'w-4 h-4' : 'w-3 h-3'} /> Suspended
        </span>
      );
    case 'rejected':
      return (
        <span className={`${baseClasses} bg-stone-100 text-stone-600 border border-stone-200`}>
          <XCircle className={size === 'lg' ? 'w-4 h-4' : 'w-3 h-3'} /> Rejected
        </span>
      );
    default:
      return (
        <span className={`${baseClasses} bg-stone-100 text-stone-600 border border-stone-200`}>
          {status}
        </span>
      );
  }
};

// Calculate days remaining until clearing date
const getDaysUntilClearing = (clearingDate: number): number => {
  const now = Date.now();
  const diff = clearingDate - now;
  return Math.max(0, Math.ceil(diff / (24 * 60 * 60 * 1000)));
};

// Referral/Commission status badge helper
const getReferralStatusBadge = (referral: Referral) => {
  const baseClasses = 'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium';

  switch (referral.status) {
    case 'pending':
      return (
        <span className={`${baseClasses} bg-amber-100 text-amber-700 border border-amber-200`}>
          <Clock className="w-3 h-3" /> Pending
        </span>
      );
    case 'clearing': {
      const daysLeft = referral.clearingDate ? getDaysUntilClearing(referral.clearingDate) : 0;
      return (
        <span className={`${baseClasses} bg-blue-100 text-blue-700 border border-blue-200`}>
          <Hourglass className="w-3 h-3" /> Clearing ({daysLeft}d)
        </span>
      );
    }
    case 'available':
      return (
        <span className={`${baseClasses} bg-emerald-100 text-emerald-700 border border-emerald-200`}>
          <Wallet className="w-3 h-3" /> Available
        </span>
      );
    case 'paid':
      return (
        <span className={`${baseClasses} bg-rose-100 text-rose-700 border border-rose-200`}>
          <CreditCard className="w-3 h-3" /> Paid
        </span>
      );
    case 'forfeited':
      return (
        <span className={`${baseClasses} bg-red-100 text-red-700 border border-red-200`}>
          <AlertOctagon className="w-3 h-3" /> Forfeited
        </span>
      );
    case 'converted':
      return (
        <span className={`${baseClasses} bg-emerald-100 text-emerald-700 border border-emerald-200`}>
          <CheckCircle className="w-3 h-3" /> Converted
        </span>
      );
    case 'expired':
      return (
        <span className={`${baseClasses} bg-stone-100 text-stone-600 border border-stone-200`}>
          <Timer className="w-3 h-3" /> Expired
        </span>
      );
    default:
      return (
        <span className={`${baseClasses} bg-stone-100 text-stone-600 border border-stone-200`}>
          {referral.status}
        </span>
      );
  }
};

/**
 * TABS. Named after AffiliateLayout's nav so an admin and a partner describe
 * the same partner with the same words. `payouts` has no portal twin — a
 * partner requests one from their dashboard — and lives last for that reason.
 */
const TAB_IDS = [
  'overview', 'deals', 'commissions', 'orders',
  'documents', 'training', 'payouts', 'profile',
] as const;
type TabId = (typeof TAB_IDS)[number];

/** Same chips PartnerDealsPanel uses, so a link reads identically on both tabs. */
const LINK_STATUS_CHIP: Record<string, { cls: string; label: string }> = {
  active: { cls: 'bg-emerald-100 text-emerald-700', label: 'Live' },
  paused: { cls: 'bg-stone-200 text-stone-600', label: 'Paused' },
  expired: { cls: 'bg-amber-100 text-amber-700', label: 'Expired' },
  exhausted: { cls: 'bg-red-100 text-red-700', label: 'Fully claimed' },
};

/** Overview summarises; the full list lives on Deals & links. */
const OVERVIEW_LINK_CAP = 5;

/** Small section card — one look for every panel on the page. */
const Card: React.FC<{
  title?: string;
  subtitle?: React.ReactNode;
  icon?: React.FC<any>;
  actions?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}> = ({ title, subtitle, icon: Icon, actions, className = '', children }) => (
  <div className={`rounded-2xl border border-stone-200 bg-white ${className}`}>
    {(title || actions) && (
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-stone-100 px-5 py-4">
        <div className="min-w-0">
          <h2 className="flex items-center gap-1.5 text-sm font-semibold text-stone-900">
            {Icon && <Icon size={15} className="text-stone-400" />}
            {title}
          </h2>
          {subtitle && <p className="mt-0.5 text-[11px] leading-relaxed text-stone-500">{subtitle}</p>}
        </div>
        {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
      </div>
    )}
    <div className="p-5">{children}</div>
  </div>
);

/**
 * Page shell — black bar, ruby underline, light content. Shared by the loading,
 * not-found and loaded states.
 *
 * MODULE SCOPE, deliberately. Declared inside the page component it would be a
 * new component type on every render, so React would unmount and remount the
 * whole content tree on each keystroke — the documents search box would lose
 * focus mid-word and the multi-select would snap shut.
 */
const Shell: React.FC<{
  title: string;
  onBack: () => void;
  subtitle?: string;
  badges?: React.ReactNode;
  actions?: React.ReactNode;
  tabs?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, onBack, subtitle, badges, actions, tabs, children }) => (
  <div className="min-h-screen bg-black -m-3 md:-m-6">
    <div className="bg-black">
      <div className="px-4 md:px-6 py-3 flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <button
            onClick={onBack}
            className="p-2 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white shrink-0"
            title="Back to partners"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 text-[11px] text-gray-500">
              <span>Partners</span>
              <span className="opacity-50">&rsaquo;</span>
              <span className="text-gray-300 truncate">{title}</span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-base md:text-lg font-semibold text-white truncate">{title}</h1>
              {badges}
            </div>
            {subtitle && <p className="text-[11px] text-gray-400 mt-0.5">{subtitle}</p>}
          </div>
        </div>
        {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
      </div>
      {tabs && <div className="px-4 md:px-6 pb-2">{tabs}</div>}
      <div style={{ background: `linear-gradient(90deg, #e8365a, ${RUBY}, ${RUBY_DEEP})`, height: '3px' }} />
    </div>
    <div className="bg-stone-100 min-h-[calc(100vh-72px)] text-stone-800">
      <div className="px-4 md:px-6 py-4 md:py-6 max-w-[1400px] mx-auto">
        {children}
      </div>
    </div>
  </div>
);

const AffiliateDetailPage: React.FC = () => {
  const { affiliateId } = useParams<{ affiliateId: string }>();
  const navigate = useNavigate();

  /**
   * The tab lives in the URL so a reload, a bookmark, or a link pasted into a
   * ticket lands where it was left. Anything unrecognised falls back rather
   * than rendering an empty page.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab') as TabId | null;
  const activeTab: TabId = rawTab && TAB_IDS.includes(rawTab) ? rawTab : 'overview';
  const setActiveTab = (id: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', id);
    setSearchParams(next, { replace: true });
  };

  const [loading, setLoading] = useState(true);
  const [affiliate, setAffiliate] = useState<AffiliateDetail | null>(null);
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  /**
   * Which copy button last fired, keyed `<linkId>` or `<linkId>:url`. A pair of
   * booleans could only ever tick one row, so with several links on screen the
   * confirmation appeared against whichever link was copied first.
   */
  const [copiedKey, setCopiedKey] = useState('');

  // Status change modal
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [newStatus, setNewStatus] = useState<'approved' | 'suspended' | 'rejected'>('approved');
  const [statusReason, setStatusReason] = useState('');

  // Password reset modal
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [resetPasswordSuccess, setResetPasswordSuccess] = useState(false);

  /**
   * Save failures for the agreement, shown INSIDE the card. The page-level
   * banner sits at the top of the content and this card is well down the right
   * column, so a failed attach used to read as "it silently did nothing".
   */
  const [agreementError, setAgreementError] = useState<string | null>(null);
  /** Local to this card — `actionLoading` disables page-wide buttons. */
  const [agreementSaving, setAgreementSaving] = useState(false);

  // Signed documents (documents-service acceptance ledger, via affiliate-service)
  const [signedDocs, setSignedDocs] = useState<Array<{
    documentId: string; slug: string | null; title: string; versionNo: number;
    acceptedAt: string | null; artifactUrl: string | null; retired: boolean;
  }>>([]);

  // Deal picker popup
  const [showDealPicker, setShowDealPicker] = useState(false);

  // Commission editor popup
  const [showCommissionModal, setShowCommissionModal] = useState(false);
  const [cmMode, setCmMode] = useState<'flat' | 'percentage' | 'both'>('percentage');
  const [cmPercent, setCmPercent] = useState('');
  /** Held in DOLLARS while editing; converted to cents on save. */
  const [cmFlat, setCmFlat] = useState('');
  const [cmError, setCmError] = useState<string | null>(null);

  // Agreement / policy attachment
  const [documents, setDocuments] = useState<
    { documentId: string; slug: string; title: string; folderName: string; activeVersionNo: number | null }[]
  >([]);
  /** Attached documents WITH their signature state, from affiliate-service. */
  const [attachedDocs, setAttachedDocs] = useState<Array<{
    documentRef: string; title: string; versionNo: number; artifactUrl: string | null;
    accepted: boolean; acceptedVersionNo: number | null;
  }>>([]);
  /** The document being read in the popup, if any. */
  const [readingDoc, setReadingDoc] = useState<{ title: string; artifactUrl: string | null } | null>(null);
  /**
   * What is attached, held LOCALLY so a tick appears the instant it is clicked
   * rather than after a server round trip. Seeded from the affiliate on load
   * and reverted if the save fails — the server stays the source of truth, it
   * just is not in the way of the checkbox.
   */
  const [attachedRefs, setAttachedRefs] = useState<string[]>([]);

  // Deal assignment
  const [selectedDealId, setSelectedDealId] = useState<string>('');

  /**
   * EVERY link this partner holds — the standing referral plus one per deal.
   * Read here as well as inside PartnerDealsPanel because Overview has to show
   * the whole set: a partner marketing five offers holds five codes, and a
   * summary that shows only `affiliate.referralCode` claims they hold one.
   *
   * The panel keeps its own copy rather than taking these as a prop: it CREATES,
   * pauses and deletes links, so it owns a list it can refetch after a write
   * without reloading this page. The duplicate GET is one cheap read and keeps
   * the two from disagreeing mid-mutation.
   */
  const [links, setLinks] = useState<any[]>([]);

  // Payouts
  const [payouts, setPayouts] = useState<Payout[]>([]);
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [selectedPayout, setSelectedPayout] = useState<Payout | null>(null);
  const [payoutTransactionId, setPayoutTransactionId] = useState('');
  const [payoutNotes, setPayoutNotes] = useState('');

  /**
   * Merchant-processing applications this partner is carrying. Fetched lazily —
   * it is a different service (affiliate-ext) and most partners are referral
   * only, so it must never delay or break the rest of the page.
   */
  const [orders, setOrders] = useState<MerchantApplicationView[] | null>(null);
  const [ordersLoading, setOrdersLoading] = useState(false);

  // Certification (attempt history only — the current status/score badge
  // reads from affiliate.certification, already present on the detail
  // response and available without a second round trip).
  const [learner, setLearner] = useState<LearnerRecord | null>(null);
  // The certification catalogue, so each attempt below can be labelled with
  // the certification it was actually sat for, and so the activation status
  // can name which certification it refers to instead of implying there is
  // only one. Empty (not null) when it could not be loaded — attempts then
  // fall back to showing their raw trackId rather than disappearing.
  const [certTracks, setCertTracks] = useState<AcademyTrack[]>([]);

  const fetchData = useCallback(async () => {
    if (!affiliateId) return;
    try {
      setLoading(true);
      const [affiliateRes, referralsRes, dealsRes, payoutsRes, documentsRes, signedRes, foldersRes, linksRes] = await Promise.all([
        api.affiliates.admin.getAffiliate(affiliateId),
        api.affiliates.admin.getAffiliateReferrals?.(affiliateId).catch(() => ({ referrals: [] })),
        api.deals.list('true').catch(() => ({ deals: [] })),
        api.affiliates.admin.getAffiliatePayouts?.(affiliateId).catch(() => ({ payouts: [] })),
        // Live documents only — a retired one cannot be attached, and the
        // server refuses it anyway.
        documentsApi.listDocuments({ status: 'active' }).catch(() => ({ documents: [] as any[] })),
        api.affiliates.admin.getSignedDocuments(affiliateId).catch(() => ({ signed: [], attached: [] })),
        // Folder names are the grouping in the picker — agreements and
        // policies read as separate lists because they live in separate folders.
        documentsApi.listFolders().catch(() => ({ folders: [] as any[] })),
        api.affiliates.admin.listLinks(affiliateId).catch(() => ({ links: [] as any[] })),
      ]);
      const affiliateData = affiliateRes as AffiliateDetail;
      setAffiliate(affiliateData);
      setReferrals((referralsRes as any)?.referrals || []);
      setDeals((dealsRes as any)?.deals || []);
      setPayouts((payoutsRes as any)?.payouts || []);
      setLinks((linksRes as any)?.links || []);
      setSelectedDealId(affiliateData.assignedDealId || '');
      const folderNames = new Map<string, string>(
        ((foldersRes as any)?.folders || []).map((f: any) => [String(f.folderId), String(f.name)]),
      );
      setDocuments(((documentsRes as any)?.documents || []).map((d: any) => ({
        documentId: String(d.documentId),
        slug: String(d.slug),
        title: String(d.title),
        folderName: folderNames.get(String(d.folderId)) || 'Uncategorised',
        // `null` = nothing in force. Such a document cannot be attached: the
        // server refuses it, and a partner would face a screen with nothing
        // to sign.
        activeVersionNo: typeof d.activeVersionNo === 'number' ? d.activeVersionNo : null,
      })));
      setSignedDocs(((signedRes as any)?.signed || []) as any);
      setAttachedDocs(((signedRes as any)?.attached || []) as any);
      setAttachedRefs(
        affiliateData.agreementDocumentRefs
          ?? (affiliateData.agreementDocumentRef ? [affiliateData.agreementDocumentRef] : []),
      );

      // Calculate earnings breakdown from referrals
      const refs = (referralsRes as any)?.referrals || [];
      const clearingEarnings = refs
        .filter((r: Referral) => r.status === 'clearing')
        .reduce((sum: number, r: Referral) => sum + (r.commission || r.commissionAmount || 0), 0);
      const availableEarnings = refs
        .filter((r: Referral) => r.status === 'available')
        .reduce((sum: number, r: Referral) => sum + (r.commission || r.commissionAmount || 0), 0);
      const forfeitedEarnings = refs
        .filter((r: Referral) => r.status === 'forfeited')
        .reduce((sum: number, r: Referral) => sum + (r.commission || r.commissionAmount || 0), 0);

      setAffiliate({
        ...affiliateData,
        clearingEarnings: affiliateData.clearingEarnings ?? clearingEarnings,
        availableEarnings: affiliateData.availableEarnings ?? availableEarnings,
        forfeitedEarnings: affiliateData.forfeitedEarnings ?? forfeitedEarnings,
      });
    } catch (err: any) {
      setError(err?.message || 'Failed to load affiliate');
    } finally {
      setLoading(false);
    }
  }, [affiliateId]);

  useEffect(() => {
    if (affiliateId) {
      void fetchData();
    }
  }, [affiliateId, fetchData]);

  useEffect(() => {
    if (!affiliate?.email) return;
    let cancelled = false;
    void (async () => {
      // The catalogue first: it decides WHICH certification the activation
      // status below is about. Previously this passed a hardcoded TRACK_ID,
      // so once the activating flag was moved to a different certification
      // this page silently reported on a certification that no longer
      // activated anyone. Resolved from `activatesPartner` instead; if the
      // catalogue can't be loaded we send no trackId at all and let the
      // service fall back to its own default rather than assert a wrong one.
      let tracks: AcademyTrack[] = [];
      try {
        tracks = (await academyApi.listTracks()).tracks || [];
      } catch { /* titles degrade to raw ids; the attempt list still renders */ }
      if (cancelled) return;
      setCertTracks(tracks);

      const activatingTrackId = tracks.find((t) => t.activatesPartner === true)?.trackId;
      try {
        const record = await academyApi.getLearner(
          'affiliate', affiliate.email.toLowerCase(), activatingTrackId,
        );
        if (!cancelled) setLearner(record);
      } catch {
        if (!cancelled) setLearner(null);
      }
    })();
    return () => { cancelled = true; };
  }, [affiliate?.email]);

  /**
   * Merchant applications, once — and only once the Orders tab is opened.
   * affiliate-ext has no per-partner endpoint, so the queue is read whole and
   * narrowed here by `assignedIsoId`.
   */
  useEffect(() => {
    if (activeTab !== 'orders' || orders !== null || !affiliateId) return;
    let cancelled = false;
    setOrdersLoading(true);
    void merchantApi.listApplications()
      .then((res) => {
        if (cancelled) return;
        setOrders((res.applications || []).filter((a) => a.assignedIsoId === affiliateId));
      })
      .catch(() => { if (!cancelled) setOrders([]); })
      .finally(() => { if (!cancelled) setOrdersLoading(false); });
    return () => { cancelled = true; };
  }, [activeTab, orders, affiliateId]);

  const handleStatusChange = async () => {
    if (!affiliateId || !affiliate) return;
    setActionLoading('status');
    try {
      await api.affiliates.admin.updateStatus(affiliateId, {
        status: newStatus,
        reason: statusReason || undefined,
      });
      await fetchData();
      setShowStatusModal(false);
      setStatusReason('');
    } catch (err: any) {
      setError(err?.message || 'Failed to update status');
    } finally {
      setActionLoading(null);
    }
  };

  const handleResetPassword = async () => {
    if (!affiliateId) return;
    setActionLoading('reset');
    try {
      await api.affiliates.admin.resetPassword?.(affiliateId);
      setResetPasswordSuccess(true);
    } catch (err: any) {
      setError(err?.message || 'Failed to reset password');
    } finally {
      setActionLoading(null);
    }
  };

  /**
   * Save the attachment list the moment it changes — there is no Save button.
   * The previous version had one, and a selection that was never saved looked
   * exactly like a save that silently failed.
   *
   * The tick updates FIRST and the write happens in the background: no page
   * reload, no spinner over the whole screen. Only the per-document signature
   * badges are re-read afterwards, because only they can have changed. If the
   * write fails the list snaps back to what the server still holds.
   *
   * Detaching leaves everything already signed intact: the acceptance ledger
   * is append-only and lives in documents-service, not here.
   */
  const handleAgreementChange = async (refs: string[]) => {
    if (!affiliateId) return;

    const previous = attachedRefs;
    setAttachedRefs(refs);          // optimistic — the checkbox answers instantly
    setAgreementSaving(true);
    setAgreementError(null);

    try {
      await api.affiliates.admin.setAgreement(affiliateId, refs);

      // Refresh ONLY the attachment statuses. `fetchData()` would re-read the
      // whole page and flip it back to its loading state.
      const fresh = await api.affiliates.admin.getSignedDocuments(affiliateId);
      setAttachedDocs((fresh?.attached || []) as any);
      setSignedDocs((fresh?.signed || []) as any);
      setAffiliate((prev) => (prev ? { ...prev, agreementDocumentRefs: refs } : prev));
    } catch (err: any) {
      setAttachedRefs(previous);    // the server did not take it — say so honestly
      // API Gateway answers a route that DOES NOT EXIST with 403 + a bare
      // `{"message":"Missing Authentication Token"}` — no `error` field, so
      // handleResponse surfaces it as "Request Failed: Forbidden". Either
      // wording means affiliate-service has not been deployed with this
      // endpoint yet, which beats a gateway string that reads like a
      // permissions problem.
      const raw = err?.message || '';
      const notDeployed = /missing authentication token/i.test(raw)
        || /request failed:\s*forbidden/i.test(raw);
      setAgreementError(
        notDeployed
          ? 'Nothing was saved — this endpoint is not deployed yet. Deploy affiliate-service to dev, then try again.'
          : raw || 'Failed to update the attached documents',
      );
    } finally {
      setAgreementSaving(false);
    }
  };

  const handleAssignDeal = async (dealId?: string | null) => {
    if (!affiliateId) return;
    // The picker passes the id it was clicked with; `undefined` keeps the old
    // behaviour of saving whatever is currently selected.
    const next = dealId === undefined ? (selectedDealId || null) : dealId;
    setActionLoading('deal');
    try {
      setSelectedDealId(next || '');
      await api.affiliates.admin.assignDeal?.(affiliateId, next);
      await fetchData();
    } catch (err: any) {
      setError(err?.message || 'Failed to assign deal');
    } finally {
      setActionLoading(null);
    }
  };

  /** Seed the editor from what the partner is actually on. */
  const openCommissionModal = () => {
    if (!affiliate) return;
    setCmMode(affiliate.commissionMode || 'percentage');
    setCmPercent(String(affiliate.percentageRate ?? 0));
    setCmFlat(((affiliate.flatFee ?? 0) / 100).toFixed(2));
    setCmError(null);
    setShowCommissionModal(true);
  };

  /**
   * Commission was READ-ONLY on this page until now — the rate could only be
   * changed by editing the record elsewhere, which is why it kept drifting out
   * of step with the deal it is paid on.
   *
   * Only the fields the chosen mode actually uses are sent: writing a stale
   * flat fee alongside a percentage-only partner is how a $0 flat fee ends up
   * quietly attached to somebody paid on percentage.
   */
  const handleSaveCommission = async () => {
    if (!affiliateId) return;
    const pct = Number(cmPercent || 0);
    const flatCents = Math.round(Number(cmFlat || 0) * 100);

    if (cmMode !== 'flat' && (!Number.isFinite(pct) || pct < 0 || pct > 100)) {
      setCmError('The percentage must be between 0 and 100.');
      return;
    }
    // The server silently drops a flat fee over $1000 rather than erroring, so
    // it is refused here where it can still be explained.
    if (cmMode !== 'percentage' && (!Number.isFinite(flatCents) || flatCents < 0 || flatCents > 100000)) {
      setCmError('The flat fee must be between $0.00 and $1,000.00.');
      return;
    }

    setActionLoading('commission');
    setCmError(null);
    try {
      await api.affiliates.admin.updateCommission(affiliateId, {
        commissionMode: cmMode,
        ...(cmMode !== 'percentage' ? { flatFee: flatCents } : {}),
        ...(cmMode !== 'flat' ? { percentageRate: pct } : {}),
      });
      await fetchData();
      setShowCommissionModal(false);
    } catch (err: any) {
      setCmError(err?.message || 'Failed to update the commission');
    } finally {
      setActionLoading(null);
    }
  };

  /**
   * Merchant-processing access. The same switch as the partners list, brought
   * onto the record it describes — the Orders tab is empty and unexplained
   * without it.
   */
  const handleToggleIso = async () => {
    if (!affiliateId || !affiliate) return;
    const next = !affiliate.isInternalIso;
    setAffiliate((prev) => (prev ? { ...prev, isInternalIso: next } : prev));
    setActionLoading('iso');
    try {
      await api.affiliates.admin.setIsoStatus(affiliateId, { isInternalIso: next });
    } catch (err: any) {
      setAffiliate((prev) => (prev ? { ...prev, isInternalIso: !next } : prev));
      setError(err?.message || 'Failed to change merchant-processing access');
    } finally {
      setActionLoading(null);
    }
  };

  const handleProcessPayout = async () => {
    if (!selectedPayout) return;
    setActionLoading('payout');
    try {
      await api.affiliates.admin.processPayout(selectedPayout.id, {
        status: 'completed',
        paymentReference: payoutTransactionId || undefined,
        notes: payoutNotes || undefined,
      });
      await fetchData();
      setShowPayoutModal(false);
      setSelectedPayout(null);
      setPayoutTransactionId('');
      setPayoutNotes('');
    } catch (err: any) {
      setError(err?.message || 'Failed to process payout');
    } finally {
      setActionLoading(null);
    }
  };

  // Certification title for an attempt's trackId. Falls back to the raw id
  // rather than a blank or a guessed name: an unrecognised id (an archived
  // certification, or one the catalogue fetch missed) is still more useful
  // to an admin than no label, and never claims to be a certification it
  // isn't.
  const trackTitle = (id?: string): string => {
    if (!id) return 'Unknown certification';
    return certTracks.find((t) => t.trackId === id)?.title || id;
  };
  const activatingTrackTitle = certTracks.find((t) => t.activatesPartner === true)?.title;

  // Frontend URL for referral links (not super-admin URL)
  const FRONTEND_URL = merchantAppUrl();

  const copyValue = async (text: string, key: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey((k) => (k === key ? '' : k)), 2000);
    } catch {
      // Clipboard is blocked in some embedded contexts. Say the code rather
      // than failing silently — it can still be read off the screen.
      setError(`Copy is blocked here — the value is ${text}`);
    }
  };

  /**
   * The URL a partner actually sends. Taken from the server's `shareUrl` when
   * it is there: the standing link and a deal link differ by parameter
   * (`?ref=` vs `?code=`), and rebuilding it here is how that quietly gets
   * wrong. The fallback is the standing-link shape only.
   */
  const shareUrlFor = (link: any): string =>
    link?.shareUrl || `${FRONTEND_URL}/#/signup?ref=${link?.code || affiliate?.referralCode || ''}`;

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateTime = (timestamp: number) => {
    return new Date(timestamp).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const goBack = () => navigate('/affiliates');

  if (loading) {
    return (
      <Shell title="Loading…" onBack={goBack}>
        <RubyLoader variant="page" />
      </Shell>
    );
  }

  if (!affiliate) {
    return (
      <Shell title="Not found" onBack={goBack}>
        <div className="text-center py-24">
          <p className="text-stone-500">Affiliate not found</p>
          <button
            onClick={() => navigate('/affiliates')}
            className="mt-4 font-medium"
            style={{ color: RUBY }}
          >
            Back to Affiliates
          </button>
        </div>
      </Shell>
    );
  }

  /**
   * Documents with nothing published are NOT offered — attaching one is
   * refused by the server, and it would put a partner in front of a screen
   * with nothing to sign. The count is surfaced under the picker so a missing
   * document reads as "not published yet", not "went missing".
   */
  const unpublishedCount = documents.filter((d) => d.activeVersionNo === null).length;

  /** Picker options, grouped by folder so agreements and policies read apart. */
  const attachmentOptions = documents
    .filter((d) => d.activeVersionNo !== null)
    .slice()
    .sort((a, b) => a.folderName.localeCompare(b.folderName) || a.title.localeCompare(b.title))
    .map((doc) => ({ value: doc.slug, label: doc.title, subtitle: doc.slug, group: doc.folderName }));

  /**
   * The server refuses to approve a partner with no agreement attached
   * (affiliate-service admin.updateStatus). Mirrored here so the button is
   * disabled rather than failing with a 400 after the confirm modal.
   */
  const agreementAttached = attachedRefs.length > 0;
  const certPassed = affiliate.certification?.status === 'passed';

  const assignedDeal = deals.find((d) => d.id === affiliate.assignedDealId);
  const standingDealLabel = assignedDeal
    ? `${assignedDeal.name} · ${dealMoney(effectiveDealPriceCents(assignedDeal))}/mo`
    : null;

  const commissionLabel = (() => {
    const pct = `${affiliate.percentageRate ?? 0}%`;
    const flat = dealMoney(affiliate.flatFee ?? 0);
    if (affiliate.commissionMode === 'percentage') return pct;
    if (affiliate.commissionMode === 'flat') return flat;
    if (affiliate.commissionMode === 'both') return `${pct} + ${flat}`;
    return '—';
  })();

  /**
   * Standing link first, then the deal links. The fallback row exists because
   * `listLinks` can be empty for a reason that is not "this partner has no
   * links" — a failed read, or a service that predates the endpoint — and the
   * standing code lives on the affiliate record either way, so it is still
   * shown rather than leaving the card blank.
   */
  const sortedLinks = [...links].sort(
    (a, b) => (b.isBaseReferral ? 1 : 0) - (a.isBaseReferral ? 1 : 0),
  );
  const overviewLinks = (sortedLinks.length
    ? sortedLinks
    : [{ linkId: 'standing', code: affiliate.referralCode, isBaseReferral: true, displayStatus: 'active' }]
  ).slice(0, OVERVIEW_LINK_CAP);

  const pendingPayouts = payouts.filter((p) => p.status === 'pending').length;
  const unsignedDocs = attachedRefs.filter(
    (ref) => !attachedDocs.find((d) => d.documentRef === ref)?.accepted,
  ).length;

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'deals', label: 'Deals & links' },
    { id: 'commissions', label: 'Commissions' },
    { id: 'orders', label: 'Orders' },
    { id: 'documents', label: 'Documents', badge: unsignedDocs },
    { id: 'training', label: 'Training' },
    { id: 'payouts', label: 'Payouts', badge: pendingPayouts },
    { id: 'profile', label: 'Profile' },
  ];

  /* ------------------------------------------------------------------ *
   * Shared fragments
   * ------------------------------------------------------------------ */

  const statusActionButtons = (
    <div className="flex flex-wrap gap-2">
      <button
        onClick={() => setShowResetPasswordModal(true)}
        className="inline-flex items-center gap-1.5 rounded-lg border border-stone-300 px-3 py-2 text-xs font-semibold text-stone-700 hover:bg-stone-50"
      >
        <Key className="w-3.5 h-3.5" /> Reset password
      </button>
      {affiliate.status === 'pending' && (
        <>
          <button
            onClick={() => { setNewStatus('approved'); setShowStatusModal(true); }}
            disabled={!agreementAttached}
            title={agreementAttached ? undefined : 'Attach an agreement or policy first'}
            className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <CheckCircle className="w-3.5 h-3.5" /> {certPassed ? 'Approve' : 'Approve — bypass exam'}
          </button>
          <button
            onClick={() => { setNewStatus('rejected'); setShowStatusModal(true); }}
            className="inline-flex items-center gap-1.5 rounded-lg bg-stone-600 px-3 py-2 text-xs font-semibold text-white hover:bg-stone-700"
          >
            <XCircle className="w-3.5 h-3.5" /> Reject
          </button>
        </>
      )}
      {affiliate.status === 'approved' && (
        <button
          onClick={() => { setNewStatus('suspended'); setShowStatusModal(true); }}
          className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-3 py-2 text-xs font-semibold text-white hover:bg-red-700"
        >
          <Ban className="w-3.5 h-3.5" /> Suspend
        </button>
      )}
      {affiliate.status === 'suspended' && (
        <button
          onClick={() => { setNewStatus('approved'); setShowStatusModal(true); }}
          disabled={!agreementAttached}
          title={agreementAttached ? undefined : 'Attach an agreement or policy first'}
          className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <CheckCircle className="w-3.5 h-3.5" /> {certPassed ? 'Reactivate' : 'Reactivate — bypass exam'}
        </button>
      )}
    </div>
  );

  /**
   * What is standing between this partner and selling, in one list. These
   * warnings used to be scattered under whichever button they blocked, so a
   * pending partner's blockers were only discoverable by scrolling.
   */
  const blockers: Array<{ text: string; tab?: TabId }> = [];
  if (!agreementAttached) {
    blockers.push({
      text: `Nothing to sign is attached — ${affiliate.name} cannot be approved until at least one agreement or policy is.`,
      tab: 'documents',
    });
  }
  if (unsignedDocs > 0) {
    blockers.push({
      text: `${unsignedDocs} attached document${unsignedDocs > 1 ? 's are' : ' is'} still awaiting their signature. They meet it at sign-in.`,
      tab: 'documents',
    });
  }
  if (!certPassed && affiliate.status !== 'rejected') {
    blockers.push({
      text: affiliate.certification?.bypassReason === 'legacy'
        ? `${affiliate.name} was approved before the certification exam existed — no exam is on record.`
        : `Certification not passed (${CERT_LABEL[affiliate.certification?.status || 'not_started']}). Approving overrides the exam gate.`,
      tab: 'training',
    });
  }
  if (pendingPayouts > 0) {
    blockers.push({
      text: `${pendingPayouts} payout request${pendingPayouts > 1 ? 's are' : ' is'} waiting to be processed.`,
      tab: 'payouts',
    });
  }

  const kpis = [
    { label: 'Referrals', value: String(affiliate.referralCount ?? 0), icon: Users, tone: 'text-stone-600' },
    { label: 'Conversions', value: String(affiliate.conversionCount ?? 0), icon: TrendingUp, tone: 'text-emerald-600' },
    {
      label: 'Conversion rate',
      value: affiliate.referralCount
        ? `${Math.round(((affiliate.conversionCount ?? 0) / affiliate.referralCount) * 100)}%`
        : '—',
      icon: Percent,
      tone: 'text-rose-600',
    },
    { label: 'Earned all time', value: dealMoney(affiliate.totalEarnings ?? 0), icon: DollarSign, tone: 'text-emerald-600' },
    { label: 'Paid out', value: dealMoney(affiliate.paidEarnings ?? 0), icon: CreditCard, tone: 'text-rose-600' },
  ];

  /* ------------------------------------------------------------------ *
   * Tabs
   * ------------------------------------------------------------------ */

  const overviewTab = (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        {kpis.map((k) => {
          const Icon = k.icon;
          return (
            <div key={k.label} className="rounded-2xl border border-stone-200 bg-white p-4">
              <div className="flex items-center gap-2">
                <Icon className={`w-4 h-4 ${k.tone}`} />
                <span className="text-[11px] font-semibold uppercase tracking-wider text-stone-400">{k.label}</span>
              </div>
              <p className="mt-1.5 font-mono text-2xl font-bold text-stone-900">{k.value}</p>
            </div>
          );
        })}
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        {/* The standing referral, summarised. Everything editable about it is
            one click away in Deals & links — there is no second copy here. */}
        {/* EVERY link this partner holds, not just the standing one. A partner
            marketing four offers carries four codes; showing only
            `affiliate.referralCode` said they carried one. Each row is the same
            summary their own portal shows them, so the two agree. */}
        <Card
          title="Referral links"
          icon={Link}
          subtitle={
            overviewLinks.length
              ? <>One link per deal, each with its own code — plus the standing referral, which is always on with no cap or expiry.</>
              : 'The standing referral is always on. Assign a deal to give this partner a link for it.'
          }
          className="lg:col-span-2"
          actions={
            <button
              onClick={() => setActiveTab('deals')}
              className="text-xs font-semibold"
              style={{ color: RUBY }}
            >
              Manage links
            </button>
          }
        >
          <div className="space-y-2.5">
            {overviewLinks.map((l) => {
              const deal = deals.find((d) => d.id === l.dealId);
              const chip = LINK_STATUS_CHIP[l.displayStatus] || LINK_STATUS_CHIP.active;
              // The brief: what a merchant clicking this link actually gets.
              const brief = [
                deal ? `${dealMoney(effectiveDealPriceCents(deal))}/mo` : null,
                deal && typeof deal.freeLocationQuota === 'number'
                  ? `${deal.freeLocationQuota} location${deal.freeLocationQuota === 1 ? '' : 's'}`
                  : null,
                deal?.packageIds?.length ? `${deal.packageIds.length} packages` : null,
                l.isBaseReferral
                  ? 'No cap'
                  : l.remainingUses === null || l.remainingUses === undefined
                    ? 'Unlimited seats'
                    : `${l.remainingUses} of ${l.maxUses} seats left`,
                l.isBaseReferral
                  ? null
                  : l.expiresAt
                    ? `expires ${formatDate(l.expiresAt)}`
                    : 'no expiry',
              ].filter(Boolean).join(' · ');

              return (
                <div
                  key={l.linkId}
                  className="rounded-xl border px-4 py-3"
                  style={l.isBaseReferral
                    ? { borderColor: 'rgba(220,20,60,0.25)', background: 'rgba(220,20,60,0.04)' }
                    : { borderColor: '#e7e5e4' }}
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="truncate text-sm font-semibold text-stone-900">
                          {l.isBaseReferral ? 'Standing referral link' : (l.label || l.deal?.name || deal?.name || l.dealId)}
                        </span>
                        <span
                          className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${l.isBaseReferral ? '' : chip.cls}`}
                          style={l.isBaseReferral ? { background: 'rgba(220,20,60,0.12)', color: RUBY_DEEP } : undefined}
                        >
                          {l.isBaseReferral ? 'Always on' : chip.label}
                        </span>
                      </div>
                      <p className="mt-0.5 text-[11px] text-stone-500">
                        {brief}
                        {l.isBaseReferral && commissionLabel !== '—' && ` · pays ${commissionLabel}`}
                      </p>
                    </div>

                    <div className="flex shrink-0 items-center gap-1.5">
                      <button
                        onClick={() => copyValue(l.code, l.linkId)}
                        title="Copy the code"
                        className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 font-mono text-[11px] transition ${
                          copiedKey === l.linkId
                            ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                            : 'border-stone-300 text-stone-600 hover:bg-stone-50'
                        }`}
                      >
                        {l.code}
                        {copiedKey === l.linkId ? <Check size={11} /> : <Copy size={11} className="opacity-60" />}
                      </button>
                      <button
                        onClick={() => copyValue(shareUrlFor(l), `${l.linkId}:url`)}
                        title={shareUrlFor(l)}
                        className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[11px] font-semibold transition ${
                          copiedKey === `${l.linkId}:url`
                            ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                            : 'border-stone-300 text-stone-600 hover:bg-stone-50'
                        }`}
                      >
                        {copiedKey === `${l.linkId}:url` ? <Check size={11} /> : <Link size={11} />}
                        {copiedKey === `${l.linkId}:url` ? 'Copied' : 'Copy link'}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Capped, with the count — the full list is one click away rather
                than pushing everything else on this tab off the screen. */}
            {links.length > OVERVIEW_LINK_CAP && (
              <button
                onClick={() => setActiveTab('deals')}
                className="w-full rounded-xl border border-dashed border-stone-300 py-2.5 text-xs font-semibold text-stone-500 hover:bg-stone-50"
              >
                View all {links.length} links in Deals &amp; links
              </button>
            )}
          </div>
        </Card>

        <Card title="Needs you" icon={AlertTriangle}>
          {blockers.length === 0 ? (
            <p className="flex items-center gap-2 text-sm text-emerald-700">
              <CheckCircle className="w-4 h-4" /> Nothing outstanding.
            </p>
          ) : (
            <ul className="space-y-2.5">
              {blockers.map((b) => (
                <li key={b.text} className="flex items-start gap-2 text-xs text-stone-600">
                  <AlertTriangle className="mt-0.5 w-3.5 h-3.5 shrink-0 text-amber-500" />
                  <span>
                    {b.text}
                    {b.tab && (
                      <button
                        onClick={() => setActiveTab(b.tab!)}
                        className="ml-1.5 font-semibold"
                        style={{ color: RUBY }}
                      >
                        Open
                      </button>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <Card title="Earnings" icon={Wallet} subtitle="Where this partner's money currently sits.">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <div className="rounded-lg border border-blue-100 bg-blue-50 p-4">
            <div className="mb-1 flex items-center gap-2">
              <Hourglass className="w-4 h-4 text-blue-500" />
              <p className="text-xs font-medium text-blue-600">Clearing</p>
            </div>
            <p className="font-mono text-xl font-bold text-blue-700">{dealMoney(affiliate.clearingEarnings ?? 0)}</p>
            <p className="mt-1 text-[10px] text-blue-500">Waiting period</p>
          </div>
          <div className="rounded-lg border border-emerald-100 bg-emerald-50 p-4">
            <div className="mb-1 flex items-center gap-2">
              <Wallet className="w-4 h-4 text-emerald-500" />
              <p className="text-xs font-medium text-emerald-600">Available</p>
            </div>
            <p className="font-mono text-xl font-bold text-emerald-700">{dealMoney(affiliate.availableEarnings ?? 0)}</p>
            <p className="mt-1 text-[10px] text-emerald-500">Ready for payout</p>
          </div>
          <div className="rounded-lg border border-rose-100 bg-rose-50 p-4">
            <div className="mb-1 flex items-center gap-2">
              <CreditCard className="w-4 h-4 text-rose-500" />
              <p className="text-xs font-medium text-rose-600">Paid</p>
            </div>
            <p className="font-mono text-xl font-bold text-rose-700">{dealMoney(affiliate.paidEarnings ?? 0)}</p>
            <p className="mt-1 text-[10px] text-rose-500">Already paid out</p>
          </div>
          <div className="rounded-lg border border-red-100 bg-red-50 p-4">
            <div className="mb-1 flex items-center gap-2">
              <AlertOctagon className="w-4 h-4 text-red-500" />
              <p className="text-xs font-medium text-red-600">Forfeited</p>
            </div>
            <p className="font-mono text-xl font-bold text-red-700">{dealMoney(affiliate.forfeitedEarnings ?? 0)}</p>
            <p className="mt-1 text-[10px] text-red-500">Lost commissions</p>
          </div>
        </div>
      </Card>
    </div>
  );

  const dealsTab = (
    <div className="space-y-4">
      <PartnerDealsPanel
        affiliateId={affiliateId!}
        sections={['deals']}
        standingDealLabel={standingDealLabel}
        onChangeStandingDeal={() => setShowDealPicker(true)}
        standingCommissionLabel={commissionLabel}
        onEditCommission={openCommissionModal}
      />

      {/* The full terms of whatever the standing link currently sells. Kept
          because "Launch offer · $49.00/mo" on the row above does not say what
          a merchant actually receives. */}
      {assignedDeal && (
        <Card
          title="What the standing link sells"
          icon={FileText}
          subtitle={<>Deals are built in Inventory &rsaquo; Sales &rsaquo; Deals; this reads that record live.</>}
          actions={
            <>
              <button
                onClick={() => navigate(`/deals/${assignedDeal.id}`)}
                className="rounded-lg border border-stone-300 px-3 py-1.5 text-xs font-semibold text-stone-700 hover:bg-stone-50"
              >
                Open this deal
              </button>
              <button
                onClick={() => navigate('/deals/new')}
                className="rounded-lg px-3 py-1.5 text-xs font-semibold text-white"
                style={{ background: RUBY }}
              >
                Build a new deal
              </button>
            </>
          }
        >
          <dl className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
            {[
              { label: 'Price', value: `${dealMoney(effectiveDealPriceCents(assignedDeal))}/mo` },
              {
                label: 'Locations included',
                value: typeof assignedDeal.freeLocationQuota === 'number' ? String(assignedDeal.freeLocationQuota) : '—',
              },
              {
                label: 'Extra location',
                value: typeof assignedDeal.additionalLocationPriceCents === 'number'
                  ? `${dealMoney(assignedDeal.additionalLocationPriceCents)}/mo`
                  : '—',
              },
              {
                label: 'Packages',
                value: assignedDeal.packageIds?.length ? `${assignedDeal.packageIds.length} included` : 'None',
              },
              {
                label: 'Devices',
                value: (assignedDeal.deviceSkuIds?.length || assignedDeal.devicePackageIds?.length)
                  ? `${assignedDeal.deviceSkuIds?.length || assignedDeal.devicePackageIds?.length} offered`
                  : 'None',
              },
            ].map((f) => (
              <div key={f.label} className="rounded-lg bg-stone-50 px-4 py-3">
                <dt className="text-[10px] font-semibold uppercase tracking-wider text-stone-400">{f.label}</dt>
                <dd className="mt-0.5 text-sm font-medium text-stone-800">{f.value}</dd>
              </div>
            ))}
          </dl>
        </Card>
      )}

      <Card
        title="Commission on the standing link"
        icon={Percent}
        subtitle="What this partner earns when a merchant signs up through the always-on code. A deal link with its own commission plan overrides this."
        actions={
          <button
            onClick={openCommissionModal}
            className="rounded-lg px-3 py-1.5 text-xs font-semibold text-white"
            style={{ background: RUBY }}
          >
            Edit
          </button>
        }
      >
        <dl className="grid grid-cols-2 gap-4 md:grid-cols-3">
          <div className="rounded-lg bg-stone-50 px-4 py-3">
            <dt className="text-[10px] font-semibold uppercase tracking-wider text-stone-400">Type</dt>
            <dd className="mt-0.5 text-sm font-medium capitalize text-stone-800">{affiliate.commissionMode}</dd>
          </div>
          {(affiliate.commissionMode === 'percentage' || affiliate.commissionMode === 'both') && (
            <div className="rounded-lg bg-stone-50 px-4 py-3">
              <dt className="text-[10px] font-semibold uppercase tracking-wider text-stone-400">Percentage rate</dt>
              <dd className="mt-0.5 font-mono text-sm font-medium text-stone-800">{affiliate.percentageRate}%</dd>
            </div>
          )}
          {(affiliate.commissionMode === 'flat' || affiliate.commissionMode === 'both') && (
            <div className="rounded-lg bg-stone-50 px-4 py-3">
              <dt className="text-[10px] font-semibold uppercase tracking-wider text-stone-400">Flat fee</dt>
              <dd className="mt-0.5 font-mono text-sm font-medium text-stone-800">{dealMoney(affiliate.flatFee ?? 0)}</dd>
            </div>
          )}
        </dl>
      </Card>
    </div>
  );

  const commissionsTab = (
    <div className="space-y-4">
      <PartnerDealsPanel affiliateId={affiliateId!} sections={['commissions']} />

      {/* Referrals Table */}
      <div className="overflow-hidden rounded-2xl border border-stone-200 bg-white">
        <div className="border-b border-stone-100 px-5 py-4">
          <h2 className="text-sm font-semibold text-stone-900">
            Referrals &amp; commissions{' '}
            <span className="text-[11px] font-normal text-stone-400">(legacy — pre-ledger history)</span>
          </h2>
          <p className="mt-0.5 text-[11px] text-stone-500">
            Everything paid before the commission ledger above existed. Kept because deleting it would hide it.
          </p>
        </div>
        {referrals.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-stone-200 bg-stone-50 text-left text-xs text-stone-500">
                  <th className="px-5 py-3 font-medium">Lead</th>
                  <th className="px-5 py-3 font-medium">Status</th>
                  <th className="px-5 py-3 font-medium">Date</th>
                  <th className="px-5 py-3 font-medium">Clearing</th>
                  <th className="px-5 py-3 text-right font-medium">Commission</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {referrals.map((ref) => (
                  <tr key={ref.id} className="text-sm">
                    <td className="px-5 py-3">
                      <p className="font-medium text-stone-800">{ref.leadName || 'Unknown'}</p>
                      <p className="text-xs text-stone-500">{ref.leadEmail}</p>
                    </td>
                    <td className="px-5 py-3">{getReferralStatusBadge(ref)}</td>
                    <td className="px-5 py-3 text-stone-600">{formatDate(ref.createdAt)}</td>
                    <td className="px-5 py-3 text-xs text-stone-600">
                      {ref.status === 'clearing' && ref.clearingDate ? (
                        <div>
                          <p className="font-medium text-blue-600">{getDaysUntilClearing(ref.clearingDate)} days left</p>
                          <p className="text-stone-400">Clears {formatDate(ref.clearingDate)}</p>
                        </div>
                      ) : ref.status === 'available' && ref.clearedAt ? (
                        <p className="text-emerald-600">Cleared {formatDate(ref.clearedAt)}</p>
                      ) : ref.status === 'paid' && ref.paidAt ? (
                        <p className="text-rose-600">Paid {formatDate(ref.paidAt)}</p>
                      ) : ref.status === 'forfeited' && ref.forfeitedAt ? (
                        <div>
                          <p className="text-red-600">Forfeited {formatDate(ref.forfeitedAt)}</p>
                          {ref.forfeitReason && (
                            <p className="max-w-[150px] truncate text-stone-400" title={ref.forfeitReason}>
                              {ref.forfeitReason}
                            </p>
                          )}
                        </div>
                      ) : (
                        <span className="text-stone-400">-</span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-right">
                      {(ref.commission || ref.commissionAmount) ? (
                        <span className={`font-mono font-medium ${
                          ref.status === 'forfeited' ? 'text-red-500 line-through' : 'text-stone-800'
                        }`}>
                          {dealMoney(ref.commission || ref.commissionAmount || 0)}
                        </span>
                      ) : (
                        <span className="text-stone-400">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="py-12 text-center text-sm text-stone-400">No referrals yet</div>
        )}
      </div>
    </div>
  );

  const ordersTab = (
    <div className="space-y-4">
      <Card
        title="Merchant processing"
        icon={Building2}
        subtitle="Whether this partner can be given merchant-processing applications to carry. The partner sees an Orders page either way — it tells them plainly when they are not set up."
      >
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="text-sm font-medium text-stone-800">
              {affiliate.isInternalIso ? 'Internal ISO — can be assigned work' : 'Referral partner only'}
            </p>
            <p className="mt-0.5 text-xs text-stone-500">
              {affiliate.isInternalIso
                ? 'Appears in the assignment picker on merchant applications.'
                : 'Turn on to let this partner be assigned merchant applications.'}
            </p>
          </div>
          <button
            role="switch"
            aria-checked={!!affiliate.isInternalIso}
            aria-label={`${affiliate.isInternalIso ? 'Remove' : 'Grant'} internal ISO access for ${affiliate.name}`}
            onClick={handleToggleIso}
            disabled={actionLoading === 'iso' || (affiliate.status !== 'approved' && !affiliate.isInternalIso)}
            title={affiliate.status !== 'approved' && !affiliate.isInternalIso
              ? 'Approve this partner first'
              : undefined}
            className={`relative h-6 w-11 shrink-0 rounded-full transition-colors disabled:opacity-40 ${
              affiliate.isInternalIso ? '' : 'bg-stone-300'
            }`}
            style={affiliate.isInternalIso ? { background: RUBY } : undefined}
          >
            <span
              className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${
                affiliate.isInternalIso ? 'left-[22px]' : 'left-0.5'
              }`}
            />
          </button>
        </div>

        {affiliate.isoCapabilities && (
          <dl className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-4">
            {[
              { label: 'Accepting orders', value: affiliate.isoCapabilities.acceptingOrders === false ? 'Paused' : 'Yes' },
              { label: 'States', value: affiliate.isoCapabilities.states?.join(', ') || 'Any' },
              { label: 'MCC codes', value: affiliate.isoCapabilities.mccCodes?.join(', ') || 'Any' },
              { label: 'Note', value: affiliate.isoCapabilities.note || '—' },
            ].map((f) => (
              <div key={f.label} className="rounded-lg bg-stone-50 px-4 py-3">
                <dt className="text-[10px] font-semibold uppercase tracking-wider text-stone-400">{f.label}</dt>
                <dd className="mt-0.5 truncate text-sm font-medium text-stone-800" title={f.value}>{f.value}</dd>
              </div>
            ))}
          </dl>
        )}
      </Card>

      <div className="overflow-hidden rounded-2xl border border-stone-200 bg-white">
        <div className="border-b border-stone-100 px-5 py-4">
          <h2 className="text-sm font-semibold text-stone-900">Applications carried by this partner</h2>
          <p className="mt-0.5 text-[11px] text-stone-500">
            The same list the partner sees on their Orders page. Read-only here — an application is worked from
            the merchant panel on its own order.
          </p>
        </div>
        {ordersLoading ? (
          <RubyLoader variant="section" />
        ) : !orders?.length ? (
          <p className="py-10 text-center text-sm text-stone-400">
            {affiliate.isInternalIso
              ? 'No applications assigned to this partner yet.'
              : 'Not set up for merchant processing.'}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-stone-200 bg-stone-50 text-left text-xs text-stone-500">
                  <th className="px-5 py-3 font-medium">Location</th>
                  <th className="px-5 py-3 font-medium">Status</th>
                  <th className="px-5 py-3 font-medium">Waiting on</th>
                  <th className="px-5 py-3 font-medium">Assigned</th>
                  <th className="px-5 py-3 font-medium">Outstanding docs</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {orders.map((app) => (
                  <tr key={app.applicationId} className="text-sm">
                    <td className="px-5 py-3">
                      <p className="font-medium text-stone-800">{app.locationName || app.locationId}</p>
                      <p className="font-mono text-[11px] text-stone-400">{app.tenantId}</p>
                    </td>
                    <td className="px-5 py-3">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                        MERCHANT_STATUS_CHIP[app.status] || 'bg-stone-200 text-stone-700'
                      }`}>
                        {MERCHANT_STATUS_LABEL[app.status] || app.status}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-xs text-stone-600">{app.summary?.waitingOn || '—'}</td>
                    <td className="px-5 py-3 text-xs text-stone-600">
                      {app.assignedAt ? formatDate(app.assignedAt) : '—'}
                    </td>
                    <td className="px-5 py-3 text-xs text-stone-600">
                      {app.outstandingDocuments?.length || 0}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  const documentsTab = (
    <div className="grid gap-4 lg:grid-cols-2">
      {/* Agreements & Policies — the gate the partner meets at sign-in */}
      <Card
        title="Agreements & policies"
        icon={FileSignature}
        subtitle="Everything this partner must sign. They meet each one at sign-in and cannot reach the portal until all are accepted. Changes save immediately. Publishing a new material version in Documents asks them again automatically; minor edits do not."
      >
        <MultiSelectDropdown
          options={attachmentOptions}
          value={attachedRefs}
          onChange={handleAgreementChange}
          placeholder="Nothing attached — choose documents"
          searchPlaceholder="Search agreements and policies..."
          summarize={(sel) => (sel.length === 1 ? sel[0].label : `${sel.length} attached`)}
        />

        {agreementSaving && (
          <p className="mt-2 flex items-center gap-1.5 text-xs text-stone-400">
            <RubyGemMark size={12} drawing /> Saving…
          </p>
        )}

        {unpublishedCount > 0 && (
          <p className="mt-2 text-[11px] text-stone-400">
            {unpublishedCount} document{unpublishedCount > 1 ? 's are' : ' is'} not listed —
            {unpublishedCount > 1 ? ' they have' : ' it has'} no published version yet.
            Publish in Documents to attach {unpublishedCount > 1 ? 'them' : 'it'}.
          </p>
        )}

        {agreementError && (
          <div className="mt-3 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-xs text-red-700">
            <AlertTriangle className="mt-0.5 w-3.5 h-3.5 shrink-0" />
            <span>{agreementError}</span>
          </div>
        )}

        {/* What is attached, with each one's signature status. */}
        {attachedRefs.length === 0 ? (
          <p className="mt-3 flex items-start gap-1.5 px-1 text-xs text-amber-600">
            <AlertTriangle className="mt-0.5 w-3.5 h-3.5 shrink-0" />
            <span>Nothing attached. This partner cannot be approved until at least one document is attached.</span>
          </p>
        ) : (
          <div className="mt-4 space-y-2">
            {attachedRefs.map((ref) => {
              const live = attachedDocs.find((d) => d.documentRef === ref);
              const title = live?.title
                || documents.find((d) => d.slug === ref)?.title
                || ref;
              return (
                <div key={ref} className="flex items-center gap-3 rounded-lg border border-stone-200 bg-stone-50 px-3 py-2.5">
                  <FileText className="w-4 h-4 shrink-0 text-stone-400" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-stone-800">{title}</p>
                    <p className="text-[11px] text-stone-500">
                      {live ? `Version ${live.versionNo}` : ref}
                    </p>
                  </div>

                  {live?.accepted ? (
                    <span className="shrink-0 inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-100 px-2 py-0.5 text-[10px] font-medium text-emerald-700">
                      <CheckCircle className="w-3 h-3" /> Signed v{live.acceptedVersionNo ?? live.versionNo}
                    </span>
                  ) : (
                    <span className="shrink-0 inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-700">
                      <Clock className="w-3 h-3" /> Awaiting signature
                    </span>
                  )}

                  <button
                    onClick={() => setReadingDoc({ title, artifactUrl: live?.artifactUrl ?? null })}
                    title="Read"
                    className="shrink-0 rounded-lg p-1.5 text-stone-400 transition hover:bg-white hover:text-stone-700"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleAgreementChange(attachedRefs.filter((r) => r !== ref))}
                    disabled={agreementSaving}
                    title="Remove"
                    className="shrink-0 rounded-lg p-1.5 text-stone-400 transition hover:bg-white hover:text-red-600 disabled:opacity-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Signed documents — the executed record, both sides can read it */}
      <Card
        title="Signed documents"
        icon={FileText}
        subtitle={`Everything signed between RubyTill and ${affiliate.name}. Each row opens the exact version they agreed to — not whatever is current today. The partner sees this same list in their portal.`}
      >
        {signedDocs.length === 0 ? (
          <p className="py-2 text-sm text-stone-400">Nothing signed yet.</p>
        ) : (
          <div className="divide-y divide-stone-100 overflow-hidden rounded-lg border border-stone-200">
            {signedDocs.map((doc) => (
              <div key={`${doc.documentId}-${doc.versionNo}-${doc.acceptedAt}`} className="flex items-center gap-3 px-4 py-3">
                <div className="shrink-0 rounded-lg bg-stone-100 p-2">
                  <FileText className="w-4 h-4 text-stone-500" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-stone-800">{doc.title}</p>
                  <p className="text-xs text-stone-500">
                    Version {doc.versionNo}
                    {doc.acceptedAt ? ` · signed ${new Date(doc.acceptedAt).toLocaleString()}` : ''}
                    {doc.retired ? ' · document retired' : ''}
                  </p>
                </div>
                {doc.artifactUrl ? (
                  <a
                    href={doc.artifactUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="shrink-0 inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition hover:bg-rose-50"
                    style={{ color: RUBY }}
                  >
                    <Download className="w-3.5 h-3.5" /> Open
                  </a>
                ) : (
                  // Published before the freeze pipeline existed: there
                  // is genuinely no file, and a dead link would be worse.
                  <span className="shrink-0 text-xs text-stone-400">No file</span>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );

  const trainingTab = (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card
        title="Certification"
        subtitle={activatingTrackTitle
          ? <>Activation status for <span className="font-medium text-stone-700">{activatingTrackTitle}</span>, the certification that switches a referral link on.</>
          : 'Activation status. No certification is currently marked as the one that switches a referral link on.'}
      >
        <dl className="grid grid-cols-2 gap-4">
          <div>
            <dt className="mb-1 text-xs text-stone-500">State</dt>
            <dd>{getCertificationBadge(affiliate.certification?.status)}</dd>
          </div>
          <div>
            <dt className="mb-1 text-xs text-stone-500">Best score</dt>
            <dd className="font-mono font-medium text-stone-800">
              {affiliate.certification?.bestScorePercent != null
                ? `${affiliate.certification.bestScorePercent}%`
                : '—'}
            </dd>
          </div>
        </dl>

        {affiliate.certification?.status === 'bypassed' && (
          <p className="mt-3 text-xs text-stone-500">
            {affiliate.certification.bypassReason === 'legacy'
              ? 'Approved before the certification exam existed — no admin action recorded.'
              : `Bypassed by an admin${affiliate.certification.bypassedAt ? ` on ${formatDate(affiliate.certification.bypassedAt)}` : ''}.`}
          </p>
        )}
      </Card>

      <Card title="Exam attempts" subtitle="Every certification, not only the activating one.">
        {learner?.attempts?.length ? (
          <ol className="space-y-2">
            {learner.attempts.map((a) => (
              <li key={a.attemptId} className="flex items-start justify-between gap-3 text-xs text-stone-500">
                {/* Each attempt is labelled with the certification it was
                    actually sat for. This endpoint returns attempts across
                    every track, so an unlabelled date/score list under a
                    single heading showed a specialisation's failure as if it
                    were a failure of the activating exam. */}
                <span className="min-w-0">
                  <span className="block truncate font-medium text-stone-700">{trackTitle(a.trackId)}</span>
                  <span className="block">{formatDateTime(a.startedAt)}</span>
                </span>
                <span className={`shrink-0 font-mono ${
                  a.passed ? 'font-medium text-emerald-600'
                    : a.scorePercent != null ? 'font-medium text-red-600' : 'text-stone-400'
                }`}
                >
                  {a.scorePercent != null ? `${a.scorePercent}%` : 'In progress'}
                </span>
              </li>
            ))}
          </ol>
        ) : (
          <p className="text-xs text-stone-400">No exam attempts yet.</p>
        )}
      </Card>
    </div>
  );

  const payoutsTab = (
    <div className="overflow-hidden rounded-2xl border border-stone-200 bg-white">
      <div className="flex items-center justify-between border-b border-stone-100 px-5 py-4">
        <div>
          <h2 className="flex items-center gap-1.5 text-sm font-semibold text-stone-900">
            <CreditCard size={15} className="text-stone-400" /> Payout requests
          </h2>
          <p className="mt-0.5 text-[11px] text-stone-500">
            Requested by the partner from their own dashboard against their available balance
            ({dealMoney(affiliate.availableEarnings ?? 0)} right now).
          </p>
        </div>
        {pendingPayouts > 0 && (
          <span className="rounded-full bg-amber-100 px-2 py-1 text-xs font-medium text-amber-700">
            {pendingPayouts} pending
          </span>
        )}
      </div>
      {payouts.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-stone-200 bg-stone-50 text-left text-xs text-stone-500">
                <th className="px-5 py-3 font-medium">Date</th>
                <th className="px-5 py-3 font-medium">Amount</th>
                <th className="px-5 py-3 font-medium">PayPal</th>
                <th className="px-5 py-3 font-medium">Status</th>
                <th className="px-5 py-3 text-right font-medium">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {payouts.map((payout) => (
                <tr key={payout.id} className="text-sm">
                  <td className="px-5 py-3 text-stone-600">{formatDate(payout.requestedAt)}</td>
                  <td className="px-5 py-3 font-mono font-medium text-stone-800">{dealMoney(payout.amount)}</td>
                  <td className="px-5 py-3 text-stone-600">{payout.paypalEmail || '-'}</td>
                  <td className="px-5 py-3">
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                      payout.status === 'completed' || payout.status === 'processed'
                        ? 'bg-emerald-100 text-emerald-700'
                        : payout.status === 'pending'
                        ? 'bg-amber-100 text-amber-700'
                        : payout.status === 'processing'
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-red-100 text-red-700'
                    }`}>
                      {payout.status}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right">
                    {payout.status === 'pending' ? (
                      <button
                        onClick={() => {
                          setSelectedPayout(payout);
                          setShowPayoutModal(true);
                        }}
                        className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-emerald-700"
                      >
                        Process
                      </button>
                    ) : payout.transactionId ? (
                      <span className="text-xs text-stone-500" title={payout.transactionId}>
                        Ref: {payout.transactionId.slice(0, 10)}...
                      </span>
                    ) : (
                      <span className="text-stone-400">-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="py-12 text-center text-sm text-stone-400">No payout requests yet</div>
      )}
    </div>
  );

  const profileTab = (
    <div className="grid gap-4 lg:grid-cols-3">
      <Card title="Profile" icon={User} className="lg:col-span-2">
        <dl className="grid gap-4 sm:grid-cols-2">
          {[
            { icon: User, label: 'Name', value: affiliate.name },
            { icon: Mail, label: 'Email', value: affiliate.email },
            ...(affiliate.phone ? [{ icon: Phone, label: 'Phone', value: affiliate.phone }] : []),
            ...(affiliate.company ? [{ icon: Building, label: 'Company', value: affiliate.company }] : []),
            { icon: Calendar, label: 'Joined', value: formatDate(affiliate.createdAt) },
            ...(affiliate.lastLoginAt
              ? [{ icon: Clock, label: 'Last login', value: formatDateTime(affiliate.lastLoginAt) }]
              : []),
            ...(affiliate.paypalEmail
              ? [{ icon: Wallet, label: 'PayPal', value: affiliate.paypalEmail }]
              : []),
          ].map((f) => {
            const Icon = f.icon;
            return (
              <div key={f.label} className="flex items-start gap-3">
                <Icon className="mt-0.5 w-4 h-4 shrink-0 text-stone-400" />
                <div className="min-w-0">
                  <dt className="text-[10px] font-semibold uppercase tracking-wider text-stone-400">{f.label}</dt>
                  <dd className="truncate text-sm font-medium text-stone-800">{f.value}</dd>
                </div>
              </div>
            );
          })}
          {affiliate.website && (
            <div className="flex items-start gap-3">
              <Globe className="mt-0.5 w-4 h-4 shrink-0 text-stone-400" />
              <div className="min-w-0">
                <dt className="text-[10px] font-semibold uppercase tracking-wider text-stone-400">Website</dt>
                <dd className="truncate">
                  <a
                    href={affiliate.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-medium"
                    style={{ color: RUBY }}
                  >
                    {affiliate.website}
                  </a>
                </dd>
              </div>
            </div>
          )}
        </dl>
      </Card>

      <Card title="Account" icon={Key} subtitle="The same actions as the header, kept here so the record and its controls sit together.">
        {statusActionButtons}
        {affiliate.status === 'suspended' && affiliate.suspendReason && (
          <p className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-xs text-red-700">
            Suspended{affiliate.suspendedAt ? ` on ${formatDate(affiliate.suspendedAt)}` : ''}: {affiliate.suspendReason}
          </p>
        )}
        {affiliate.status === 'approved' && affiliate.approvedAt && (
          <p className="mt-4 text-xs text-stone-500">
            Approved on {formatDate(affiliate.approvedAt)}
            {affiliate.approvedBy ? ` by ${affiliate.approvedBy}` : ''}.
          </p>
        )}
      </Card>
    </div>
  );

  const tabContent: Record<TabId, React.ReactNode> = {
    overview: overviewTab,
    deals: dealsTab,
    commissions: commissionsTab,
    orders: ordersTab,
    documents: documentsTab,
    training: trainingTab,
    payouts: payoutsTab,
    profile: profileTab,
  };

  return (
    <>
      <Shell
        title={affiliate.name}
        onBack={goBack}
        subtitle={`${affiliate.email} · joined ${formatDate(affiliate.createdAt)}${
          affiliate.lastLoginAt ? ` · last seen ${formatDateTime(affiliate.lastLoginAt)}` : ''
        }`}
        badges={
          <>
            {getStatusBadge(affiliate.status, 'lg')}
            {getCertificationBadge(affiliate.certification?.status)}
            {affiliate.isInternalIso && (
              <span className="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-rose-100 px-2 py-0.5 text-xs font-medium text-rose-700">
                <Building2 className="w-3 h-3" /> Internal ISO
              </span>
            )}
          </>
        }
        actions={statusActionButtons}
        tabs={<StaffSubtabs tabs={tabs} active={activeTab} onChange={setActiveTab} />}
      >
        {error && (
          <div className="mb-4 flex items-center justify-between rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="font-medium text-red-600 hover:text-red-800">Dismiss</button>
          </div>
        )}
        {tabContent[activeTab]}
      </Shell>

      {/* Read one attached document without leaving the page. */}
      {readingDoc && (
        <div className="fixed inset-0 z-[9998] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-stone-900/60 backdrop-blur-sm" onClick={() => setReadingDoc(null)} />
          <div className="relative flex h-[80vh] w-full max-w-3xl flex-col rounded-2xl border border-stone-200 bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-stone-200 px-5 py-4">
              <h3 className="truncate pr-4 text-base font-semibold text-stone-800">{readingDoc.title}</h3>
              <div className="flex shrink-0 items-center gap-2">
                {readingDoc.artifactUrl && (
                  <a
                    href={readingDoc.artifactUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium hover:bg-rose-50"
                    style={{ color: RUBY }}
                  >
                    <Download className="w-3.5 h-3.5" /> Open in a tab
                  </a>
                )}
                <button
                  onClick={() => setReadingDoc(null)}
                  className="rounded-lg p-2 text-stone-400 hover:bg-stone-100 hover:text-stone-600"
                >
                  <XCircle className="w-5 h-5" />
                </button>
              </div>
            </div>
            {readingDoc.artifactUrl ? (
              <iframe src={readingDoc.artifactUrl} title={readingDoc.title} className="w-full flex-1 rounded-b-2xl border-0" />
            ) : (
              // A version published before the freeze pipeline has no artifact.
              // Say so rather than showing an empty frame.
              <div className="flex flex-1 items-center justify-center px-8 text-center text-sm text-stone-400">
                This version has no readable copy attached.
              </div>
            )}
          </div>
        </div>
      )}

      <DealPickerModal
        open={showDealPicker}
        deals={deals}
        currentDealId={affiliate.assignedDealId || ''}
        onSelect={(dealId) => handleAssignDeal(dealId)}
        onClose={() => setShowDealPicker(false)}
      />

      {/* Commission editor */}
      {showCommissionModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl border border-stone-200 bg-white p-6 shadow-2xl">
            <h2 className="mb-1 text-lg font-bold text-stone-800">Commission on the standing link</h2>
            <p className="mb-5 text-xs text-stone-500">
              What {affiliate.name} earns from a merchant who signs up through the always-on referral code.
              A deal link carrying its own commission plan is paid on that plan instead.
            </p>

            <label className="mb-1 block text-xs font-semibold text-stone-600">Type</label>
            <div className="mb-4 flex gap-2">
              {(['percentage', 'flat', 'both'] as const).map((m) => (
                <button
                  key={m}
                  onClick={() => setCmMode(m)}
                  className={`flex-1 rounded-lg border px-3 py-2 text-xs font-semibold capitalize transition ${
                    cmMode === m ? 'text-white' : 'border-stone-300 text-stone-600 hover:bg-stone-50'
                  }`}
                  style={cmMode === m ? { background: RUBY, borderColor: RUBY } : undefined}
                >
                  {m}
                </button>
              ))}
            </div>

            <div className="mb-4 grid grid-cols-2 gap-3">
              {cmMode !== 'flat' && (
                <div>
                  <label className="mb-1 block text-xs font-semibold text-stone-600">Percentage rate</label>
                  <div className="flex items-center gap-2">
                    <input
                      value={cmPercent}
                      onChange={(e) => setCmPercent(e.target.value.replace(/[^0-9.]/g, ''))}
                      inputMode="decimal"
                      className="w-full rounded-lg border border-stone-300 bg-white px-3 py-2 font-mono text-sm text-stone-800 outline-none focus:border-stone-400"
                    />
                    <span className="text-sm text-stone-500">%</span>
                  </div>
                </div>
              )}
              {cmMode !== 'percentage' && (
                <div>
                  <label className="mb-1 block text-xs font-semibold text-stone-600">Flat fee</label>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-stone-500">$</span>
                    <input
                      value={cmFlat}
                      onChange={(e) => setCmFlat(e.target.value.replace(/[^0-9.]/g, ''))}
                      inputMode="decimal"
                      className="w-full rounded-lg border border-stone-300 bg-white px-3 py-2 font-mono text-sm text-stone-800 outline-none focus:border-stone-400"
                    />
                  </div>
                  <p className="mt-1 text-[10px] text-stone-400">Up to $1,000.00</p>
                </div>
              )}
            </div>

            {cmError && (
              <div className="mb-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-xs text-red-700">
                <AlertTriangle className="mt-0.5 w-3.5 h-3.5 shrink-0" />
                <span>{cmError}</span>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => setShowCommissionModal(false)}
                className="flex-1 rounded-xl bg-stone-100 px-4 py-2.5 text-sm font-medium text-stone-700 transition hover:bg-stone-200"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveCommission}
                disabled={actionLoading === 'commission'}
                className="flex-1 rounded-xl px-4 py-2.5 text-sm font-medium text-white transition disabled:opacity-50"
                style={{ background: RUBY }}
              >
                {actionLoading === 'commission' ? <span className="flex justify-center"><RubyGemMark size={20} drawing color="currentColor" /></span> : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Status Change Modal */}
      {showStatusModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl border border-stone-200 bg-white p-6 shadow-2xl">
            <h2 className="mb-4 text-lg font-bold text-stone-800">
              {newStatus === 'approved' && 'Approve Affiliate'}
              {newStatus === 'suspended' && 'Suspend Affiliate'}
              {newStatus === 'rejected' && 'Reject Affiliate'}
            </h2>

            <p className="mb-4 text-stone-600">
              {newStatus === 'approved' && `Are you sure you want to approve ${affiliate.name}? They will be able to start earning commissions.`}
              {newStatus === 'suspended' && `Are you sure you want to suspend ${affiliate.name}? They will not be able to earn commissions until reactivated.`}
              {newStatus === 'rejected' && `Are you sure you want to reject ${affiliate.name}'s application?`}
            </p>

            {newStatus === 'approved' && !certPassed && (
              <div className="mb-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
                <AlertTriangle className="mt-0.5 w-4 h-4 shrink-0 text-amber-600" />
                <p className="text-xs text-amber-700">
                  {affiliate.name} has not passed the certification exam ({CERT_LABEL[affiliate.certification?.status || 'not_started']}).
                  Confirming bypasses the exam gate and records this as an admin override.
                </p>
              </div>
            )}

            {(newStatus === 'suspended' || newStatus === 'rejected') && (
              <div className="mb-4">
                <label className="mb-1 block text-sm font-medium text-stone-600">Reason (optional)</label>
                <textarea
                  value={statusReason}
                  onChange={(e) => setStatusReason(e.target.value)}
                  placeholder="Enter reason..."
                  rows={3}
                  className="w-full resize-none rounded-lg border border-stone-200 bg-stone-50 px-3 py-2 text-stone-800 placeholder-stone-400 outline-none focus:border-stone-400"
                />
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => { setShowStatusModal(false); setStatusReason(''); }}
                className="flex-1 rounded-xl bg-stone-100 px-4 py-2.5 text-sm font-medium text-stone-700 transition hover:bg-stone-200"
              >
                Cancel
              </button>
              <button
                onClick={handleStatusChange}
                disabled={actionLoading === 'status'}
                className={`flex-1 rounded-xl px-4 py-2.5 text-sm font-medium transition disabled:opacity-50 ${
                  newStatus === 'approved'
                    ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                    : newStatus === 'suspended'
                    ? 'bg-red-600 text-white hover:bg-red-700'
                    : 'bg-stone-600 text-white hover:bg-stone-700'
                }`}
              >
                {actionLoading === 'status' ? <span className="flex justify-center"><RubyGemMark size={20} drawing color="currentColor" /></span> : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetPasswordModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl border border-stone-200 bg-white p-6 shadow-2xl">
            {resetPasswordSuccess ? (
              <>
                <div className="mb-4 text-center">
                  <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100">
                    <CheckCircle className="w-8 h-8 text-emerald-600" />
                  </div>
                  <h2 className="text-lg font-bold text-stone-800">Password Reset Sent</h2>
                  <p className="mt-2 text-stone-600">
                    A password reset email has been sent to {affiliate.email}
                  </p>
                </div>
                <button
                  onClick={() => { setShowResetPasswordModal(false); setResetPasswordSuccess(false); }}
                  className="w-full rounded-xl bg-black px-4 py-2.5 text-sm font-medium text-white transition hover:bg-stone-800"
                >
                  Done
                </button>
              </>
            ) : (
              <>
                <div className="mb-4 flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100">
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                  </div>
                  <h2 className="text-lg font-bold text-stone-800">Reset Password</h2>
                </div>

                <p className="mb-6 text-stone-600">
                  This will send a password reset email to <strong>{affiliate.email}</strong>. They will need to create a new password to log in.
                </p>

                <div className="flex gap-3">
                  <button
                    onClick={() => setShowResetPasswordModal(false)}
                    className="flex-1 rounded-xl bg-stone-100 px-4 py-2.5 text-sm font-medium text-stone-700 transition hover:bg-stone-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleResetPassword}
                    disabled={actionLoading === 'reset'}
                    className="flex-1 rounded-xl bg-black px-4 py-2.5 text-sm font-medium text-white transition hover:bg-stone-800 disabled:opacity-50"
                  >
                    {actionLoading === 'reset' ? <span className="flex justify-center"><RubyGemMark size={20} drawing color="currentColor" /></span> : 'Send Reset Email'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Process Payout Modal */}
      {showPayoutModal && selectedPayout && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl border border-stone-200 bg-white p-6 shadow-2xl">
            <div className="mb-4 flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-100">
                <CreditCard className="w-5 h-5 text-emerald-600" />
              </div>
              <h2 className="text-lg font-bold text-stone-800">Process Payout</h2>
            </div>

            <div className="mb-6 rounded-lg bg-stone-50 p-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-stone-500">Affiliate</p>
                  <p className="font-medium text-stone-800">{affiliate.name}</p>
                </div>
                <div>
                  <p className="text-stone-500">Amount</p>
                  <p className="font-mono text-lg font-bold text-emerald-600">{dealMoney(selectedPayout.amount)}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-stone-500">PayPal Email</p>
                  <p className="font-medium text-stone-800">{selectedPayout.paypalEmail || affiliate.paypalEmail || 'Not provided'}</p>
                </div>
              </div>
            </div>

            <div className="mb-6 space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-stone-600">Transaction ID / Reference</label>
                <input
                  type="text"
                  value={payoutTransactionId}
                  onChange={(e) => setPayoutTransactionId(e.target.value)}
                  placeholder="PayPal transaction ID..."
                  className="w-full rounded-lg border border-stone-200 bg-stone-50 px-3 py-2 text-stone-800 placeholder-stone-400 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-stone-600">Notes (optional)</label>
                <textarea
                  value={payoutNotes}
                  onChange={(e) => setPayoutNotes(e.target.value)}
                  placeholder="Any notes about this payout..."
                  rows={2}
                  className="w-full resize-none rounded-lg border border-stone-200 bg-stone-50 px-3 py-2 text-stone-800 placeholder-stone-400 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500"
                />
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowPayoutModal(false);
                  setSelectedPayout(null);
                  setPayoutTransactionId('');
                  setPayoutNotes('');
                }}
                className="flex-1 rounded-xl bg-stone-100 px-4 py-2.5 text-sm font-medium text-stone-700 transition hover:bg-stone-200"
              >
                Cancel
              </button>
              <button
                onClick={handleProcessPayout}
                disabled={actionLoading === 'payout'}
                className="flex-1 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-emerald-700 disabled:opacity-50"
              >
                {actionLoading === 'payout' ? <span className="flex justify-center"><RubyGemMark size={20} drawing color="currentColor" /></span> : 'Mark as Paid'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AffiliateDetailPage;
